import { Box, LinearProgress, Stack, Typography } from "@mui/material";
import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useGetCanceledSubscriptionsQuery } from "src/api/services/subscriptionsApi";
import {
  LIMIT,
  ORDER,
  ORDER_BY,
  PAGE,
  PATIENT_FIRST_NAME,
  PATIENT_ID,
  PATIENT_LAST_NAME,
  STATE,
  SUBSCRIPTION_NAME,
} from "src/constants/searchParams";
import { toNumber } from "src/utils/toNumber";
import { filters } from "./utils/filters";
import Filter from "src/components/TableFilter";
import { reversedStatesMap } from "src/constants/usStates";
import DataTable from "src/components/DataTable";
import { useColumns } from "./useColumns";
import { usePagination } from "src/hooks/usePagination";

const Cancellations = () => {
  const [params] = useSearchParams();
  const page = useMemo(() => toNumber(params.get(PAGE)), [params]);
  const limit = useMemo(() => toNumber(params.get(LIMIT)), [params]);
  const columns = useColumns();
  const pagination = usePagination();

  const height = useMemo(
    () => window.innerHeight - 42 - 60 - 32 - 52 - 32 - 4,
    [],
  );

  const from = useMemo(() => {
    return page * limit;
  }, [limit, page]);

  const to = useMemo(() => {
    return page + limit - 1;
  }, [limit, page]);

  const stateFilter = useMemo(() => {
    const state = params.get(STATE);
    if (state != null) {
      return reversedStatesMap[state];
    }

    return undefined;
  }, [params]);

  const queryParams = useMemo(
    () => ({
      region: stateFilter,
      first_name: params.get(PATIENT_FIRST_NAME) ?? undefined,
      last_name: params.get(PATIENT_LAST_NAME) ?? undefined,
      patient_id: params.get(PATIENT_ID) ?? undefined,
      display_name: params.get(SUBSCRIPTION_NAME) ?? undefined,
      order_by: params.get(ORDER_BY) ?? undefined,
      ascending: params.get(ORDER) === "asc" ? true : false,
      from,
      to,
    }),
    [from, params, stateFilter, to],
  );

  const { data, isFetching } = useGetCanceledSubscriptionsQuery(queryParams);

  const { subscriptions, count } = useMemo(() => {
    if (data == null) return { subscriptions: [], count: 0 };

    return { subscriptions: data.data, count: data.count };
  }, [data]);

  return (
    <Stack gap="32px">
      <Typography variant="h4" fontWeight={600}>
        Cancellations
      </Typography>
      <Stack
        direction="row"
        gap="16px"
        height={60}
        width="100%"
        alignItems="center"
      >
        {filters.map((filter) => (
          <Filter key={filter.name} current={filter} />
        ))}
      </Stack>
      <Stack gap={0}>
        <Box height={4}>
          {isFetching ? <LinearProgress color="secondary" /> : null}
        </Box>
        <DataTable
          data={subscriptions}
          columns={columns}
          primaryKey="id"
          pagination={pagination}
          total={count}
          height={height}
        />
      </Stack>
    </Stack>
  );
};

export default Cancellations;
